import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format timestamp in reporting timezone (Asia/Kolkata by default)
 */
export function formatDate(dateString: string | null | undefined, includeTime = false): string {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';

    return new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      ...(includeTime ? { hour: '2-digit', minute: '2-digit', hour12: true } : {}),
    }).format(date);
  } catch {
    return dateString;
  }
}

/**
 * Number formatter with K, M abbreviations
 */
export function formatNumber(num: number | null | undefined): string {
  if (num === null || num === undefined) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
  return num.toLocaleString();
}

/**
 * Robust copy-to-clipboard helper with modern navigator.clipboard and textarea fallback
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // Fallback for non-secure contexts
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const successful = document.execCommand('copy');
    textArea.remove();
    return successful;
  } catch (err) {
    console.error('Failed to copy text:', err);
    return false;
  }
}

/**
 * Appends UTM parameters cleanly onto a destination URL preserving existing parameters
 */
export function buildDestinationUrl(params: {
  baseUrl: string;
  source?: string | null;
  medium?: string | null;
  campaign?: string | null;
  term?: string | null;
  content?: string | null;
}): string {
  const { baseUrl, source, medium, campaign, term, content } = params;
  if (!baseUrl.trim()) return '';

  try {
    const url = new URL(baseUrl.trim());

    if (source && source.trim()) url.searchParams.set('utm_source', source.trim());
    if (medium && medium.trim()) url.searchParams.set('utm_medium', medium.trim());
    if (campaign && campaign.trim()) url.searchParams.set('utm_campaign', campaign.trim());
    if (term && term.trim()) url.searchParams.set('utm_term', term.trim());
    if (content && content.trim()) url.searchParams.set('utm_content', content.trim());

    return url.toString();
  } catch {
    return baseUrl;
  }
}
